import React, {
  useState, useCallback, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { isNullOrUndefined } from 'ontraccr-common/lib/Common';

import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import OnTraccrButton from '../../common/buttons/OnTraccrButton';

import CompanySettingsCard from '../CompanySettingsCard';
import CompanyEditRow from '../CompanyEditRow';

import { saveCompanySettings } from '../state/settings.actions';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import ProjectsService from '../../projects/state/projects.service';

export default function ProjectNumber() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settings.company.settings) ?? {};
  const { projectNumberAnchor } = settings;

  const [draftAnchor, setDraftAnchor] = useState(projectNumberAnchor ?? null);
  const [previewNextNumber, setPreviewNextNumber] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    setDraftAnchor(projectNumberAnchor ?? null);
  }, [projectNumberAnchor]);

  useEffect(() => {
    let cancelled = false;
    const loadPreview = async () => {
      if (
        draftAnchor === null
        || draftAnchor === ''
        || !Number.isInteger(draftAnchor)
        || draftAnchor < 1
      ) {
        setPreviewNextNumber(null);
        return;
      }
      setPreviewLoading(true);
      try {
        const {
          data: {
            nextNumber,
          } = {},
        } = await ProjectsService.getNextProjectNumber(draftAnchor);
        if (!cancelled) setPreviewNextNumber(nextNumber);
      } catch {
        if (!cancelled) setPreviewNextNumber(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };
    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [draftAnchor]);

  const onAnchorChange = useCallback((value) => {
    if (isNullOrUndefined(value) || value === '' || Number.isNaN(value)) {
      setDraftAnchor(null);
      return;
    }
    setDraftAnchor(value);
  }, []);

  const hasUnsavedChanges = draftAnchor !== (projectNumberAnchor ?? null);

  const onSave = useCallback(() => {
    if (isNullOrUndefined(draftAnchor) || draftAnchor === '') return;
    CustomConfirmModal({
      title: `Save ${t('Project')} Number Anchor`,
      content: (
        <div>
          New
          {' '}
          {t('Project', { count: 2 }).toLowerCase()}
          {' '}
          will be assigned numbers starting from this anchor.
          Existing
          {' '}
          {t('Project').toLowerCase()}
          {' '}
          numbers will not be changed.
          {previewNextNumber && (
            <>
              <br />
              <br />
              The next
              {' '}
              {t('Project').toLowerCase()}
              {' '}
              number will be
              {' '}
              <strong>{previewNextNumber}</strong>
              .
            </>
          )}
        </div>
      ),
      onOk: () => {
        dispatch(saveCompanySettings({ projectNumberAnchor: draftAnchor }));
      },
    });
  }, [draftAnchor, previewNextNumber, t]);

  const onClear = useCallback(() => {
    CustomConfirmModal({
      title: `Clear ${t('Project')} Number Anchor`,
      content: (
        <div>
          New
          {' '}
          {t('Project', { count: 2 }).toLowerCase()}
          {' '}
          will use the previous highest number plus one.
          Existing
          {' '}
          {t('Project').toLowerCase()}
          {' '}
          numbers will not be changed.
        </div>
      ),
      onOk: () => {
        setDraftAnchor(null);
        dispatch(saveCompanySettings({ projectNumberAnchor: null }));
      },
    });
  }, [t]);

  const showDuplicateWarning = draftAnchor != null
    && previewNextNumber != null
    && String(previewNextNumber) !== String(draftAnchor);

  return (
    <Row justify="start" style={{ height: '90%' }}>
      <CompanySettingsCard
        title={`${t('Project')} Number`}
        leftHeaderView={(
          <div style={{ display: 'flex', gap: 8 }}>
            <OnTraccrButton
              title="Save"
              onClick={onSave}
              disabled={!hasUnsavedChanges || draftAnchor == null}
            />
            {projectNumberAnchor != null && (
              <OnTraccrButton
                title="Clear Anchor"
                onClick={onClear}
                type="back"
              />
            )}
          </div>
        )}
      >
        <CompanyEditRow
          title={`Set ${t('Project')} Number Anchor`}
          helpText={(
            <div>
              The
              {' '}
              {t('Project').toLowerCase()}
              {' '}
              number anchor sets the baseline for automatically assigned
              {' '}
              {t('Project').toLowerCase()}
              {' '}
              numbers.
              <br />
              <br />
              Ontraccr will assign the anchor value to the next new
              {' '}
              {t('Project').toLowerCase()}
              , then increment for each subsequent
              {' '}
              {t('Project', { count: 2 }).toLowerCase()}
              . Only applies to numeric
              {' '}
              {t('Project', { count: 2 }).toLowerCase()}
              {' '}
              numbers.
              <br />
              <br />
              This does not change existing
              {' '}
              {t('Project', { count: 2 }).toLowerCase()}
              {' '}
              numbers. If the anchor is already in use,
              Ontraccr skips to the next available number.
              <br />
              <br />
              This works with the Suggest Numbers preference in General settings.
            </div>
          )}
          divider={false}
        />
        <div style={{ maxWidth: 300, marginBottom: 16 }}>
          <OnTraccrNumberInput
            min={1}
            precision={0}
            step={1}
            value={draftAnchor}
            onChange={onAnchorChange}
            placeholder="Enter anchor number"
            style={{ marginTop: 5 }}
          />
        </div>
        {draftAnchor != null && previewNextNumber && !previewLoading && (
          <Alert
            type={showDuplicateWarning ? 'warning' : 'info'}
            showIcon
            style={{ marginBottom: 16, maxWidth: 600, borderRadius: 8 }}
            message={showDuplicateWarning
              ? `${t('Project')} number ${draftAnchor} already exists; new ${t('Project', { count: 2 }).toLowerCase()} will start at ${previewNextNumber}.`
              : `Next ${t('Project').toLowerCase()} number will be ${previewNextNumber}.`}
          />
        )}
      </CompanySettingsCard>
    </Row>
  );
}
